// authReducer.ts
import { createSlice } from '@reduxjs/toolkit';
import { authQuery } from 'service/authQuery/authQuery';

export type UserType = 'institution' | 'candidate' | 'admin' | 'recruiter';

export interface AcademicInfo {
  course: string;
  department: string;
  yearOfStudy: number | null;
  academicAdvisor: string;
  enrollmentDate: Date | null;
  expectedGraduationDate: Date | null;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface Skill {
  skill: string;
  experience: string;
}

export interface Education {
  level: string;
  degree: string;
  college: string;
  startDate: string; // Assuming the startDate can be null
  endDate: string; // Assuming the endDate can be null
}

export interface Experience {
  overallYear: string;
}

export interface Earning {
  currentAnnualEarning: string;
  expectedAnnualEarning: string;
}

export interface JobAvailability {
  preference: string;
  startWork: string;
}

export interface Portal {
  github: string;
  linkedIn: string;
}

type Role = {
  _id: string;
  value: string;
  category: string;
  desc: string;
  active: boolean;
  __v: number;
};

type UserRole = {
  _id: string;
  name: string;
  desc: string;
  permissions: Record<string, Record<string, boolean>>;
};

type UserRoles = UserRole[];

type User = {
  _id: string;
  username: string;
  email: string;
  userType?: UserType; // Adjust this union type based on possible values for userType
  info: {
    _id: string;
    type?: 'school' | 'college' | 'university' | 'training_center'; // Adjust this union type based on possible values for type
    address?: string | Address;
    year?: string;
    contactPersonName?: string;
    contactPersonDesignation?: string;
    contactEmail?: string;
    contactPhoneNumber?: string;
    contactNumber?: string;
    dateOfBirth?: string;
    academicInfo?: AcademicInfo;
    registerNumber?: string;
    gender?: string;
    institutionId?: string;
    skills?: Skill[];
    portal?: Portal;
    education?: Education[];
    experience?: Experience;
    role?: Role; // Assuming role is a reference to another object (e.g., an ObjectId)
    earning?: Earning;
    jobAvailability?: JobAvailability;
    about?: string;
    __v?: number;
    profileImage?: string;
    numberOfEmployees?: string;
    recruiterDesignation?: string;
    recruiterName?: string;
  };
  roles?: UserRoles;
  createdAt: string;
  updatedAt: string;
  __v: number;
};

type UserState = {
  user: User;
};

// Initial state
const initialState: UserState = {
  user: {
    _id: '',
    username: '',
    email: '',
    userType: undefined,
    info: {
      _id: '',
      __v: 0
    },
    createdAt: '',
    updatedAt: '',
    __v: 0
  }
};

const userMatcher = authQuery.endpoints.userMe;

// Create slice or reducer
const UserReducer = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Add your reducers here
  },
  extraReducers: builder => {
    builder.addMatcher(userMatcher.matchFulfilled, (state, action) => {
      return { ...state, user: action.payload?.data };
    });
  }
});

export default UserReducer.reducer;
